import React, { useState, useEffect, useRef } from 'react';
import { useNavigation } from '../../contexts/NavigationContext';
import LanguageFontSelector from '../LanguageFontSelector/LanguageFontSelector';
import ThemeColorSelector from '../ThemeColorSelector/ThemeColorSelector';
import logoImage from '../../assets/imgs/2.png';
import './Header.css';

const siteNames = {
  EN: 'Reboon',
  KU: 'ڕێبوون',
  AR: 'ریبوون',
  TR: 'Reboon'
};

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentLang, setCurrentLang] = useState(() => {
    return localStorage.getItem('selectedLanguage') || 'KU';
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [partsMenuOpen, setPartsMenuOpen] = useState(false);
  const [visibleItemsCount, setVisibleItemsCount] = useState(3);
  const [isMobile, setIsMobile] = useState(false);
  const mobileMenuRef = useRef(null);
  const partsMenuRef = useRef(null);

  // Handle scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Listen for language changes from LanguageSwitcher
  useEffect(() => {
    const handleLanguageChange = (event) => {
      setCurrentLang(event.detail);
    };
    window.addEventListener('languageChange', handleLanguageChange);
    return () => window.removeEventListener('languageChange', handleLanguageChange);
  }, []);

  // Sync with LanguageSwitcher initial state
  const handleLanguageChange = (langCode) => {
    setCurrentLang(langCode);
    localStorage.setItem('selectedLanguage', langCode);
  };

  // Set RTL direction for Kurdish and Arabic
  useEffect(() => {
    const isRTL = currentLang === 'KU' || currentLang === 'AR';
    const direction = isRTL ? 'rtl' : 'ltr';

    // Set direction on html element (affects entire page)
    document.documentElement.setAttribute('dir', direction);
    document.documentElement.setAttribute('lang', currentLang.toLowerCase());

    // Also set on body for comprehensive RTL support
    document.body.setAttribute('dir', direction);
  }, [currentLang]);

  // Update document title based on current language
  useEffect(() => {
    document.title = siteNames[currentLang] || siteNames.EN;
  }, [currentLang]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        // Only close mobile menu if clicking outside and menu is open
        if (mobileMenuOpen && !event.target.closest('.mobile-menu-toggle')) {
          setMobileMenuOpen(false);
        }
      }
      // Close parts menu when clicking outside
      if (partsMenuRef.current && !partsMenuRef.current.contains(event.target)) {
        if (partsMenuOpen && !event.target.closest('.parts-button')) {
          setPartsMenuOpen(false);
        }
      }
    };

    if (mobileMenuOpen || partsMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [mobileMenuOpen, partsMenuOpen]);

  // Handle window resize and calculate visible items
  useEffect(() => {
    const calculateVisibleItems = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      if (width >= 1200) {
        setVisibleItemsCount(8); // Show all items on large screens
      } else if (width >= 1024) {
        setVisibleItemsCount(3);
      } else if (width >= 768) {
        setVisibleItemsCount(2);
      } else {
        setVisibleItemsCount(0); // On mobile, all items go in mobile menu
      }
    };

    calculateVisibleItems();
    const handleResize = () => {
      calculateVisibleItems();
      // Close mobile menu on resize to desktop
      if (window.innerWidth > 768) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }
    return () => {
      document.body.classList.remove('menu-open');
    };
  }, [mobileMenuOpen]);

  const getNavLinks = () => {
    const navTexts = {
      EN: {
        home: 'Home',
        parts: 'Parts',
        aboutUs: 'About Us',
        activities: 'Activities',
        publishings: 'Publishings',
        academia: 'Academia',
        sanad: 'Sanad',
        applications: 'Applications',
        womens: 'Womens',
        podcasts: 'Podcasts',
      },
      KU: {
        home: 'سەرەکی',
        parts: 'بەشەکان',
        aboutUs: 'دەربارەی ئێمە',
        activities: 'چالاکیەکان',
        publishings: 'بڵاوکراوەکان',
        academia: 'ئەکادیمی',
        sanad: 'سەنەد',
        applications: 'بەرنامەکان',
        womens: 'خانمانی ڕۆشنگەر',
        podcasts: 'پۆدکاستەکان',
      },
      AR: {
        home: 'الرئيسية',
        parts: 'الأقسام',
        aboutUs: 'من نحن',
        activities: 'الأنشطة',
        publishings: 'المنشورات',
        academia: 'الأكاديمية',
        sanad: 'السند',
        applications: 'الطلبات',
        womens: 'النساء',
        podcasts: 'البودكاست',
      },
      TR: {
        home: 'Ana Sayfa',
        parts: 'Bölümler',
        aboutUs: 'Hakkımızda',
        activities: 'Etkinlikler',
        publishings: 'Yayınlar',
        academia: 'Akademi',
        sanad: 'Sanad',
        applications: 'Başvurular',
        womens: 'Kadınlar',
        podcasts: 'Podcastler',
      },
    };

    const texts = navTexts[currentLang] || navTexts.EN;

    return {
      mainNavLinks: [
        { label: texts.home, href: '#home', page: 'home' },
      ],
      partsLinks: [
        { label: texts.aboutUs, href: '#about', page: 'about' },
        { label: texts.activities, href: '#activities', page: 'activities' },
        { label: texts.publishings, href: '#publishings', page: 'publishings' },
        { label: texts.academia, href: '#academia', page: 'academia' },
        { label: texts.sanad, href: '#sanad', page: 'sanad' },
        { label: texts.applications, href: '#applications', page: 'applications' },
        { label: texts.womens, href: '#womens', page: 'womens' },
        { label: texts.podcasts, href: '#podcasts', page: 'podcasts' },
      ],
      navTexts,
    };
  };

  const { mainNavLinks, partsLinks, navTexts } = getNavLinks();
  const { currentPage, navigateTo } = useNavigation();

  // Calculate which items to show directly vs hide behind Parts button
  const visiblePartsLinks = partsLinks.slice(0, visibleItemsCount);
  const hiddenPartsLinks = partsLinks.slice(visibleItemsCount);
  const showPartsButton = hiddenPartsLinks.length > 0 && !isMobile;
  const partsButtonText = navTexts[currentLang]?.parts || navTexts.EN.parts;

  const handleNavClick = (e, page) => {
    e.preventDefault();
    navigateTo(page);
    setMobileMenuOpen(false);
    setPartsMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const togglePartsMenu = () => {
    setPartsMenuOpen(!partsMenuOpen);
  };

  return (
    <header className={`header ${isScrolled ? 'header-scrolled' : ''}`}>
      <div className="header-container">
        {/* Logo and Site Name */}
        <div className="header-logo">
          <img src={logoImage} alt="Reboon Logo" className="logo-image" />
          <span className="site-name">{siteNames[currentLang] || siteNames.EN}</span>
        </div>

        {/* Navigation - Glassmorphism */}
        <nav
          className={`header-nav ${mobileMenuOpen ? 'nav-open' : ''}`}
          ref={mobileMenuRef}
        >
          <div className="nav-glass">
            <ul className="nav-list">
              {/* Main nav links (always visible) */}
              {mainNavLinks.map((link) => (
                <li key={link.page || link.href}>
                  <button
                    type="button"
                    className={`nav-link ${currentPage === link.page ? 'active' : ''}`}
                    onClick={(e) => handleNavClick(e, link.page)}
                  >
                    {link.label}
                  </button>
                </li>
              ))}
              
              {/* Visible parts links (only on desktop) */}
              {!isMobile && visiblePartsLinks.map((link) => (
                <li key={link.page || link.href}>
                  <button
                    type="button"
                    className={`nav-link ${currentPage === link.page ? 'active' : ''}`}
                    onClick={(e) => handleNavClick(e, link.page)}
                  >
                    {link.label}
                  </button>
                </li>
              ))}
              
              {/* Parts button with hidden items */}
              {showPartsButton && (
                <li className="parts-menu-wrapper" ref={partsMenuRef}>
                  <button
                    type="button"
                    className={`parts-button nav-link ${partsMenuOpen ? 'active' : ''}`}
                    onClick={togglePartsMenu}
                  >
                    {partsButtonText}
                  </button>
                  {partsMenuOpen && (
                    <ul className="parts-menu">
                      {hiddenPartsLinks.map((link) => (
                        <li key={link.page || link.href}>
                          <button
                            type="button"
                            className={`nav-link ${currentPage === link.page ? 'active' : ''}`}
                            onClick={(e) => handleNavClick(e, link.page)}
                          >
                            {link.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              )}
              
              {/* On mobile, show all links in mobile menu */}
              {isMobile && mobileMenuOpen && partsLinks.map((link) => (
                <li key={link.page || link.href}>
                  <button
                    type="button"
                    className={`nav-link ${currentPage === link.page ? 'active' : ''}`}
                    onClick={(e) => handleNavClick(e, link.page)}
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Right Side Controls */}
        <div className="header-controls">
          <LanguageFontSelector onLanguageChange={handleLanguageChange} />
          <ThemeColorSelector />
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className={`mobile-menu-toggle ${mobileMenuOpen ? 'active' : ''}`}
          aria-label="Toggle menu"
          aria-expanded={mobileMenuOpen}
          onClick={toggleMobileMenu}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </header>
  );
};

export default Header;
